// Window declaration for TS to use it with google dataLayer
declare global {
    interface Window { dataLayer: any; }
}

window.dataLayer = window.dataLayer || [];

import { Injectable } from '@angular/core';
import { ConfigService } from '../config/config.service';
import { CustomerModel } from 'src/app/models/customer-model';
import { UserSessionService } from '../user-session/user-session.service';
import { WebservicesService } from '../webservices/webservices.service';
import { Platform } from '@ionic/angular';
import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics";
interface EventParams {
    [key: string]: any;
}

@Injectable({
    providedIn: "root",
})
export class GoogleService {
    private transactionDevice;
    private customer: CustomerModel;
    private lastCustomerId: string | null = null;

    constructor(
        private config: ConfigService,
        private userSession: UserSessionService,
        private webservicesService: WebservicesService,
        private platform: Platform,
    ) {
        this.userSession.getCustomer().subscribe(
            (customer) => {
                if (customer) {
                    this.customer = customer;
                    if (customer && customer.customerId !== null) {
                        const newCustomerId = customer.customerId.toString();
                        if (newCustomerId !== this.lastCustomerId) {
                            this.lastCustomerId = newCustomerId;
                            try {
                                this.setupFirebaseAnalytics(newCustomerId);
                            } catch (e) {
                                console.log("Error setting up Firebase Analytics with new customer ID:", e);
                            }
                        }
                    }
                }
            }
        );
    }

    setup() {
        this.transactionDevice = this.config.getClientApp();
        this.setupGoogleAnalytics();
        this.setupGoogleABTesting();
        this.setUpGTM();
        try {
            this.setupFirebaseAnalytics(this.customer.customerId.toString())
        } catch (e) {
            console.log("Google Service Setup Error")
        }
    }

    private setupFirebaseAnalytics(userId: string) {
        if (!this.platform.is('capacitor')) {
            return;
        };
        FirebaseAnalytics.setUserId({
            userId: userId,
        })
    }

    private setUpGTM() {
        let GTMKey = this.config.getGTMKey();
        if (GTMKey && GTMKey !== "") {
            const script = document.createElement('script');
            script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTMKey}');`;

            document.head.appendChild(script);

            const noScript = document.createElement('noscript');
            noScript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTMKey}"
            height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
            var s = document.body.firstChild;
            s.parentNode.insertBefore(noScript, s);
        }
    }

    private setupGoogleAnalytics() {
        let UAKey = this.config.getGoogleAnalyticsKey();
        let ABKey = this.config.getGoogleABTestingKey();
        if (UAKey && UAKey !== "") {
            const script = document.createElement('script');
            script.innerHTML = `(function(i,s,o,g,r,a,m){i['GoogleAnalyticsObject']=r;i[r]=i[r]||function(){
                (i[r].q=i[r].q||[]).push(arguments)},i[r].l=1*new Date();a=s.createElement(o),
                m=s.getElementsByTagName(o)[0];a.async=1;a.src=g;m.parentNode.insertBefore(a,m)
                })(window,document,'script','https://www.google-analytics.com/analytics.js','ga');
                ga('create','${UAKey}','auto'); 
                ga('send', 'pageview');
                ga('require','${ABKey}');
           `;

            document.head.appendChild(script);
        }
    }

    private setupGoogleABTesting() {
        let ABKey = this.config.getGoogleABTestingKey();
        if (ABKey && ABKey !== "") {
            const script = document.createElement('script');
            script.innerHTML = `(function(a,s,y,n,c,h,i,d,e){s.className+=' '+y;h.start=1*new Date;
            h.end=i=function(){s.className=s.className.replace(RegExp(' ?'+y),'')};
            (a[n]=a[n]||[]).hide=h;setTimeout(function(){i();h.end=null},c);h.timeout=c;
            })(window,document.documentElement,'async-hide','dataLayer',4000,
            {'${ABKey}':true});`;
            document.head.appendChild(script);
        }
    }

    //=======================EVENTS===============================//

    loginEvent(customer_id) {
        let dataToSend = {
            customerId: customer_id,
            device: this.transactionDevice,
            event: "Login",
            transactionType: "Login"
        }
        this.sendGtmEvent(dataToSend);
    }
    // step 1
    createAccountEvent(customer_id) {

        let dataToSend = {
            event: "Registration",
            customerId: customer_id,
            device: this.transactionDevice,
            transactionType: "Registration"
        }

        this.sendGtmEvent(dataToSend);
    }
    // step 2
    createAccountCompleteEvent(data) {
        let dataToSend = {
            event: "Registration Complete",
            customerId: data.customer_id ? data.customer_id : this.customer.customerId,
            device: this.transactionDevice,
            transactionType: "Registration Complete"
        }

        this.sendGtmEvent(dataToSend);
    }


    depositEvent(data) {
        this.getEventData('deposit').then(
            (eventData: any) => {
                let dataToSend = {
                    customerId: data.customer_id ? data.customer_id : this.customer.customerId,
                    device: this.transactionDevice,
                    depositCount: eventData.transactionNumber,
                    depositAmount: data.amount,
                    depositCurrency: this.config.getConfig().DEFAULT_CURRENCY,
                    transactionId: data.transaction_id ? data.transaction_id : 'N/A',
                    paymentMethod: data.paymentMethod,
                    transactionType: "Deposit",
                    event: "Deposit"
                }
                // check for first deposit
                dataToSend.transactionType = (eventData.transactionNumber > 1) ? "Further Deposit Complete" : "First Deposit Complete";
                this.sendGtmEvent(dataToSend);

                //Trying to integrate 2 new deposit event which will trigger only once 
                //during user lifespan - first Deposit scenario specific event
                if (dataToSend.transactionType == 'First Deposit Complete') {
                    let firstDepositSuccess = {
                        first_deposit_success: true,
                        event: "first_deposit_success",
                        transactionType: "first_deposit_success"
                    };
                    this.sendGtmEvent(firstDepositSuccess);

                    let firstDepositAmount = {
                        first_deposit_amount: data.amount,
                        event: "first_deposit_amount",
                        transactionType: "first_deposit_amount"
                    };
                    this.sendGtmEvent(firstDepositAmount);
                }
            }
        )
    }

    withdrawalEvent(data) {
        this.getEventData('withdraw').then(
            (eventData: any) => {
                let dataToSend = {
                    transactionPlayerID: data.customer_id ? data.customer_id : this.customer.customerId,
                    device: this.transactionDevice,
                    transactionTotal: data.amount,
                    transactionType: "Withdrawal",
                    event: "Withdrawal"
                }
                this.sendGtmEvent(dataToSend);
            }
        )
    }

    sendPageHistoryEvent(pageName) {
        let pagePath = pageName.page ? pageName.page : pageName
        let websiteUrl = document.location.origin;
        let virtualRoute = `${websiteUrl}/${pagePath} `;
        let data = {
            event: "pageView",
            url: virtualRoute,
        };
        this.sendGtmEvent(data);
    }

    sendWebViewEvent() {
        let data = {
            'event': 'Pageview',
            'url': window.location.href
        }
        this.sendGtmEvent(data);
    }


    gameLaunched(data) {
        let dataToSend = {
            customerId: (!!this.customer) ? this.customer.customerId : "undefined",
            gameName: (data.game_name != null) ? data.game_name : data.name,
            event: "Game Launched",
            transactionType: "Game Launched"
        }
        this.sendGtmEvent(dataToSend);
    }

    loginError(data) {
        var dataToSend = {
            type: 'loginError',
            event: 'loginError',
            detail: {
                errorCode: data.errorCode,
                errorName: data.errorName,
                errorType: 'loginError',
                timestamp: new Date().getTime()
            }
        }
        this.sendGtmEvent(dataToSend);
    }

    forgotPassword(data) {
        // Forgot Password - Customer completed the forgot password process
        var dataToSend = {
            type: 'forgotPassword',
            event: 'forgotPassword',
            detail: {
                timestamp: new Date().getTime()
            }
        }
        this.sendGtmEvent(dataToSend);
    }

    conversion(data) {
        data.event = data.transactionType = 'conversion_succeeded';
        this.sendGtmEvent(data);
    }

    betPlaced(data) {
        data.event = data.transactionType = 'bet_placed';
        this.sendGtmEvent(data);
    }

    logout() {
        let customerId = (this.customer && 'customerId' in this.customer) ? this.customer.customerId : "";
        let dataToSend = {
            customerId: customerId,
            event: "Logout",
            transactionType: "Logout"
        }
        this.sendGtmEvent(dataToSend);
    }

    gameClosed() {
        let customerId = (this.customer && 'customerId' in this.customer) ? this.customer.customerId : "";
        let dataToSend = {
            customerId: customerId,
            event: "Game Closed",
            transactionType: "Game Closed"
        }
        this.sendGtmEvent(dataToSend);
    }

    //==================== END of EVENTS ======================// 
    //========================================================//



    private sendGtmEvent(data) {
        if (this.platform.is('capacitor')) {
            this.handleCapacitorFirebaseAnalytics(data);
        } else {
            window.dataLayer.push(data);
        }
    }

    private handleCapacitorFirebaseAnalytics(data) {
        const eventParams = {
            name: (data?.event?.replace(/\s/g, '_') ?? 'event').toLowerCase(),
            params: {} as EventParams,
        };

        for (const key of Object.keys(data || {})) {
            if (key !== 'event') {
                eventParams.params[key] = this.getParamValue(data, key);
            }
        }

        const paramsMapping = {
            customerId: this.customer?.customerId?.toString(),
            device: this.transactionDevice,
        };

        for (const [key, value] of Object.entries(paramsMapping)) {
            if (value && !eventParams.params[key]) {
                eventParams.params[key] = value;
            }
        }

        FirebaseAnalytics.logEvent(eventParams);
    }

    private getParamValue(data, key) {
        if (key === 'transactionType') {
            return data[key].replace(/\s/g, '_');
        }

        if (key === 'detail' && typeof data[key] === 'object') {
            const detailParams = {};
            for (const detailKey of Object.keys(data[key])) {
                detailParams[detailKey] = data[key][detailKey];
            }
            return detailParams;
        }

        if (key === 'url') {
            try {
                const url = new URL(data[key]);
                const pathSegments = url.pathname.split('/');
                return pathSegments[pathSegments.length - 1];
            } catch (e) {
                console.error('Invalid URL in data:', data[key]);
                return data[key]?.toString() || 'Invalid URL'; // Ensure a string is returned
            }
        }

        return data[key];
    }




    get transactionRegDate() {
        let transactionRegDate = this.customer.createdDate;
        return transactionRegDate;
    }

    get transactionGameEngage() {
        let transactionGameEngage;
        return transactionGameEngage;
    }

    public getEventData(eventName, customerId?, sessionToken?) {
        return new Promise((resolve, reject) => {
            let customer_id;
            let session_token = sessionToken ? sessionToken : this.userSession.sessionToken;
            if (customerId) {
                customer_id = customerId;
            } else {
                customer_id = (!!this.customer) ? this.customer.customerId : "undefined"
            }

            let params = {
                customer_id: customer_id,
                session_token: session_token
            }

            this.webservicesService.get('customer/get-transaction-info', params).subscribe(transactionData => {
                if (!!transactionData.payload) {
                    let payload = transactionData.payload;
                    let data: any = {
                        transactionRegDate: payload.transactionRegDate,
                        transactionNumber: (eventName === 'deposit') ? payload.depositCount : payload.withdrawalCount,
                        transactionLastDeposit: payload.lastDepositDate,
                        transaction30NGR: payload.last30DayWinLoss,
                        transactionLifeNGR: payload.allTimeWinLoss,
                        transactionGameEngage: payload.lastPlayedGameName
                    }
                    resolve(data);
                } else {
                    reject(new Error('Payload missing from get-transaction-info service'));
                }
            }, (err: any) => {
                reject(err);
            });
        });
    }
}
